import { ReactNode, useEffect } from 'react';
import { useCheckboxGroupOptions } from '@alltrails/denali/components/CheckboxGroup';
import { CheckboxFilterConfig } from '@alltrails/denali/components/FilterBar';
import ExploreServiceFilters from '../../types/ExploreServiceFilters';

type Args<T extends string> = {
  additionalContent?: ReactNode;
  exploreServiceFilterKey: Extract<keyof ExploreServiceFilters, 'difficulty' | 'feature' | 'routeType' | 'suitability' | 'trailTraffic'>;
  exploreServiceFilters: ExploreServiceFilters;
  getValueLabel: (value: T) => ReactNode;
  getValueSubtext?: (value: T) => ReactNode;
  label: ReactNode;
  onFiltersChange: (updatedFilters: ExploreServiceFilters) => void;
  values: T[];
};

const useCheckboxFilterConfig = <T extends string>({
  additionalContent,
  exploreServiceFilterKey,
  exploreServiceFilters,
  getValueLabel,
  getValueSubtext,
  label,
  onFiltersChange,
  values
}: Args<T>): CheckboxFilterConfig => {
  const selected = (exploreServiceFilters[exploreServiceFilterKey] as T[]) || [];
  const { onChange, options, clearOptions, selectedOptions, setOptions } = useCheckboxGroupOptions(
    values.map(value => ({
      id: value,
      selected: selected.includes(value),
      subtext: getValueSubtext?.(value),
      testId: value,
      text: getValueLabel(value)
    }))
  );

  useEffect(() => {
    const areEqual = selectedOptions.length === selected.length && selectedOptions.every(option => selected.includes(option.id as T));
    if (!areEqual) {
      setOptions(
        values.map(value => ({
          id: value,
          selected: selected.includes(value),
          subtext: getValueSubtext?.(value),
          testId: value,
          text: getValueLabel(value)
        }))
      );
    }
    // Only want to update when we get new values.
    // stringify the array since the object itself is constantly changing.
  }, [JSON.stringify(selected)]);

  useEffect(() => {
    const newFilters = { ...exploreServiceFilters };
    delete newFilters[exploreServiceFilterKey];
    if (selectedOptions.length === 0) {
      // Do nothing, act like there is no filter
    } else {
      newFilters[exploreServiceFilterKey] = selectedOptions.map(option => option.id as any); // Any cast needed since due to the generic type
    }
    onFiltersChange(newFilters);

    // Only want to trigger this the selected value changes
  }, [selectedOptions]);

  return {
    type: 'checkbox',
    id: exploreServiceFilterKey,
    filterChip: {
      onClear: clearOptions,
      selectedItems: selectedOptions?.map(option => option.text),
      testId: `${exploreServiceFilterKey}-filter-chip`,
      unselectedText: label
    },
    checkboxGroup: {
      labelText: label,
      onChange,
      options,
      testId: `${exploreServiceFilterKey}-filter`
    },
    additionalContent
  };
};

export default useCheckboxFilterConfig;
